/**
 * Utilitários de usuário — displayName + validação de senha
 */

// ── Display Name ──────────────────────────────────────────────

/**
 * Retorna o displayName se disponível, caso contrário deriva da parte
 * local do email (antes do @). Nunca retorna vazio.
 */
export const getUserDisplayName = (
  displayName: string | null | undefined,
  email: string,
): string => {
  if (displayName && displayName.trim().length > 0) return displayName.trim()
  const local = email.split('@')[0] ?? ''
  // Capitaliza primeira letra e substitui pontos/hífens por espaços
  return local.replace(/[.\-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) || email
}

// ── Força de Senha ────────────────────────────────────────────

export type SenhaForca = 'FRACA' | 'MEDIA' | 'FORTE' | 'MUITO_FORTE'

export interface SenhaValidacao {
  forca: SenhaForca
  pontos: number
  criterios: {
    tamanhoMinimo: boolean      // ≥ 8 chars
    tamanhoIdeal: boolean       // ≥ 12 chars
    temMaiuscula: boolean
    temMinuscula: boolean
    temNumero: boolean
    temEspecial: boolean
    semRepeticoes: boolean      // não tem 3+ chars iguais seguidos
  }
  sugestoes: string[]
}

/**
 * Valida a força de uma senha e retorna diagnóstico completo.
 * Usada tanto no frontend (formulários) quanto no backend (enforce mínimo).
 */
export const validarForcaSenha = (senha: string): SenhaValidacao => {
  const criterios = {
    tamanhoMinimo: senha.length >= 8,
    tamanhoIdeal: senha.length >= 12,
    temMaiuscula: /[A-Z]/.test(senha),
    temMinuscula: /[a-z]/.test(senha),
    temNumero: /[0-9]/.test(senha),
    temEspecial: /[^A-Za-z0-9]/.test(senha),
    semRepeticoes: senha.length > 0 && !/(.)\1{2,}/.test(senha),
  }

  let pontos = 0
  if (criterios.tamanhoMinimo) pontos += 20
  if (criterios.tamanhoIdeal) pontos += 10
  if (criterios.temMaiuscula) pontos += 15
  if (criterios.temMinuscula) pontos += 15
  if (criterios.temNumero) pontos += 15
  if (criterios.temEspecial) pontos += 20
  if (criterios.semRepeticoes) pontos += 5

  const forca: SenhaForca =
    pontos >= 85 ? 'MUITO_FORTE' :
    pontos >= 65 ? 'FORTE' :
    pontos >= 40 ? 'MEDIA' :
    'FRACA'

  const sugestoes: string[] = []
  if (!criterios.tamanhoMinimo) sugestoes.push('Use al menos 8 caracteres')
  if (!criterios.tamanhoIdeal) sugestoes.push('Use 12 o más caracteres para mayor seguridad')
  if (!criterios.temMaiuscula) sugestoes.push('Incluya al menos una letra mayúscula')
  if (!criterios.temMinuscula) sugestoes.push('Incluya al menos una letra minúscula')
  if (!criterios.temNumero) sugestoes.push('Incluya al menos un número')
  if (!criterios.temEspecial) sugestoes.push('Incluya al menos un carácter especial (!, @, #, etc.)')
  if (!criterios.semRepeticoes) sugestoes.push('Evite repetir el mismo carácter tres o más veces seguidas')

  return { forca, pontos, criterios, sugestoes }
}

/**
 * Verifica se a senha atende o mínimo obrigatório do sistema.
 * Retorna true se válida, false se deve ser rejeitada.
 */
export const senhaAtendeMínimo = (senha: string): boolean => {
  const { criterios } = validarForcaSenha(senha)
  return (
    criterios.tamanhoMinimo &&
    criterios.temMaiuscula &&
    criterios.temMinuscula &&
    criterios.temNumero
  )
}
